import { BCDropdown, IComboboxSelectedChangeEventArgs, IDropdownProps } from '@bimser/components';
import { IKeyTextValue } from '@bimser/components/dist/BCCombobox/entities/IComboboxSelectedChangeEventArgs';
import { FormItemTypes, IDictionary, IItem } from '@bimser/core';
import IconEmpty from '@bimser/icons/16/empty-icon';
import GetIcon16ByName from '@bimser/icons/lib/getIcon16ByName';
import { JSX } from 'react';
import { DisplayMode, SizeType } from '../../../../entities/common';
import ComboBoxBase from '../../base/ComboBoxBase';
import Styles from './assets/styles.scss';
import { IDropdownProperties } from './entities/IDropdownProperties';
import { Portal } from 'react-portal';

const pickerPopupId = 'dropdownPopupContainer';

export default class Dropdown extends ComboBoxBase {
	sizeType: SizeType;
	displayMode: DisplayMode;

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.Dropdown;
		this.getDefaultValue = this.getDefaultValue.bind(this);
		this.displayMode = 'icontext';

		this.subscribe('trigger:OnValueChanged', (value: string) => {
			const changedItem = this.items.find(i => i.key === value);
			const selectedItemValues = [changedItem.value];
			const items = this.items.map(item => ({
				...item,
				selected: item.key === value
			}));
			this.__proxyChanging.selectedItemValues = selectedItemValues;
			this.__proxyChanging.items = items;
		});
	}

	__loadProperties(data: IDropdownProperties) {
		super.__loadProperties(data);
		this.__proxyChanged.sizeType = data.sizeType;
		this.__proxyChanged.displayMode = data.displayMode;
	}

	protected triggerValueChanged(args: IComboboxSelectedChangeEventArgs) {
		if (args.data) {
			const value = (args.data as IKeyTextValue).value;
			const changedItem = this.items.find(
				i => i.value == (args.data as IKeyTextValue).value
			) || { value: '' };
			this.__proxyChanging.selectedItemValues = [value];
			this.__proxyChanging.items = this.items.map(item =>
				Object.assign({}, item, {
					selected: item.value.toString() === changedItem.value.toString()
				})
			);
		} else {
			this.__proxyChanging.items = this.items.map(item =>
				Object.assign({}, item, { selected: false })
			);
		}
	}

	getDefaultValue() {
		const selectedItem = this.items.find(i => i.selected);
		if (selectedItem) {
			return selectedItem.value;
		}
	}

	__getItems(): IItem[] {
		return this.__sortItems(
			Object.values(this.__itemsCollection).map(i => ({
				key: i.key,
				value: i.value,
				selected: this.selectedItemValues?.includes(i.value),
				text: i.text.getText(),
				icon: GetIcon16ByName(i.icon, IconEmpty).info
			}))
		);
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			sizeType: this.sizeType,
			displayMode: this.displayMode,
			options: this.__getItems()
		};
	}

	__getSizeType() {
		return this.sizeType ?? this.__form.sizeType;
	}

	__renderPopupContainer() {
		if (!this.__form.getShadowDomMode()) {
			return (
				<Portal>
					<div id={pickerPopupId}></div>
				</Portal>
			);
		}
	}

	__render(children?: JSX.Element) {
		return super.__render(
			<div className={Styles.dropDown}>
				{this.__renderPopupContainer()}
				<BCDropdown
					selectedItemShowType={
						this.dataSourceType === 'dynamic' ? 'text' : this.displayMode
					}
					isHiddenDefaultItem
					tabIndex={this.tabIndex}
					items={this.__getItems()}
					defaultValue={this.getDefaultValue()}
					disabled={this.loading || this.__isDisabled()}
					readonly={this.__isReadOnly()}
					onSelected={this.triggerValueChanged.bind(this)}
					className={Styles.dropDownStyles}
					style={{
						...this.style,
						...({ '--bgColor': this.style.backgroundColor } as any)
					}}
					size={this.__getSizeType()}
					placeholder={this.placeholder.getText()}
					getPopupContainer={this.__form.__getShadowRootContainer}
					extractPopupKey={this.__form.getShadowDomMode() && pickerPopupId}
				/>
			</div>
		);
	}
}
